import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { classifyLink } from "@/lib/app-link";
import { Link, useParams } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import { useMemo } from "react";
import type { KairoDocList, KairoDocListItem } from "../chat.utils";

interface Props {
  data: KairoDocList;
}

function formatRelativeDate(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 14) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

function compareUpdatedAt(a: KairoDocListItem, b: KairoDocListItem): number {
  const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
  const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
  return tb - ta;
}

export function ChatKairoDocList({ data }: Props) {
  // Route params let us build a doc URL from id when the AI doesn't supply one.
  // The chat route lives under /workspaces/$slug/projects/$projectSlug/..., so
  // the workspace slug arrives as `slug` (not `workspaceSlug`).
  const params = useParams({ strict: false });

  const items = useMemo(() => [...data.items].sort(compareUpdatedAt), [data.items]);

  if (items.length === 0) {
    return <p className="text-muted-foreground mt-2 text-sm italic">No documents found.</p>;
  }

  // CR-162: no bare /docs/:id fallback — outside a project the name is shown
  // without a link rather than pointing at a route that cannot render.
  const buildHref = (item: KairoDocListItem): string | null => {
    // The url comes from AI output: only follow it when it stays in the app,
    // as an app-relative path (CR-163).
    const link = classifyLink(item.url);
    if (link.kind === "internal") {
      return link.path;
    }
    if (params.slug && params.projectSlug) {
      return `/workspaces/${params.slug}/projects/${params.projectSlug}/docs/${item.id}`;
    }
    return null;
  };

  return (
    <div className="mt-2 flex flex-col gap-1">
      {data.title ? (
        <p className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
          {data.title}
        </p>
      ) : null}
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => {
          const href = buildHref(item);
          const updated = formatRelativeDate(item.updated_at);
          const rowBody = (
            <>
              <FileText className="text-muted-foreground mt-0.5 size-4 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="text-foreground font-medium group-hover:underline">
                  {item.name}
                </span>
                {updated ? (
                  <span className="text-muted-foreground ml-2 text-xs">updated {updated}</span>
                ) : null}
              </span>
            </>
          );
          const linkContent = href ? (
            <Link
              to={href}
              preload="intent"
              className="group flex items-start gap-2 rounded px-1.5 py-1 text-sm hover:bg-black/5"
            >
              {rowBody}
            </Link>
          ) : (
            <div className="flex items-start gap-2 rounded px-1.5 py-1 text-sm">{rowBody}</div>
          );
          if (!item.summary) {
            return <li key={item.id}>{linkContent}</li>;
          }
          return (
            <li key={item.id}>
              <Tooltip>
                <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                <TooltipContent side="top" className="max-w-md whitespace-normal">
                  {item.summary}
                </TooltipContent>
              </Tooltip>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
